import api from "@/lib/api";
import {
  CreateTeamSubscriptionPlanData,
  MyTeamSubscriptionsOverview,
  SubscribeResult,
  TeamMemberSubscription,
  TeamSubscriptionPlan,
  TeamSubscriptionStats,
  UpdateTeamSubscriptionPlanData,
  UpdateTeamSubscriptionPlanResult,
} from "@/types/team-subscription";

export class TeamSubscriptionService {
  async listPlans(teamId: string, includeInactive = false): Promise<TeamSubscriptionPlan[]> {
    const response = await api.get<TeamSubscriptionPlan[]>(
      `/teams/${teamId}/subscriptions/plans`,
      { params: includeInactive ? { includeInactive: "true" } : undefined },
    );
    return response.data;
  }

  async createPlan(
    teamId: string,
    data: CreateTeamSubscriptionPlanData,
  ): Promise<TeamSubscriptionPlan> {
    const response = await api.post<TeamSubscriptionPlan>(
      `/teams/${teamId}/subscriptions/plans`,
      data,
    );
    return response.data;
  }

  async updatePlan(
    teamId: string,
    planId: string,
    data: UpdateTeamSubscriptionPlanData,
  ): Promise<UpdateTeamSubscriptionPlanResult> {
    const response = await api.patch<UpdateTeamSubscriptionPlanResult>(
      `/teams/${teamId}/subscriptions/plans/${planId}`,
      data,
    );
    return response.data;
  }

  async listSubscribers(teamId: string): Promise<TeamMemberSubscription[]> {
    const response = await api.get<TeamMemberSubscription[]>(
      `/teams/${teamId}/subscriptions/members`,
    );
    return response.data;
  }

  async getMySubscription(teamId: string): Promise<TeamMemberSubscription | null> {
    const response = await api.get<TeamMemberSubscription | null>(
      `/teams/${teamId}/subscriptions/me`,
    );
    return response.data;
  }

  async subscribe(teamId: string, planId: string): Promise<SubscribeResult> {
    const response = await api.post<SubscribeResult>(
      `/teams/${teamId}/subscriptions/plans/${planId}/subscribe`,
    );
    return response.data;
  }

  async payWithCard(
    teamId: string,
    data: {
      cardToken: string;
      renewalCardToken: string;
      deviceSessionId?: string;
      backUrl?: string;
      paymentMethodId?: string;
      issuerId?: string;
      payerCpf?: string;
    },
  ): Promise<{
    status: string;
    paymentId?: string;
    mpRenewalScheduled?: boolean;
    subscription: TeamMemberSubscription;
  }> {
    const response = await api.post(`/teams/${teamId}/subscriptions/me/pay/card`, data);
    return response.data;
  }

  async cancelMySubscription(teamId: string, immediate = false): Promise<TeamMemberSubscription> {
    const response = await api.post<TeamMemberSubscription>(
      `/teams/${teamId}/subscriptions/me/cancel`,
      {},
      { params: immediate ? { immediate: "true" } : undefined },
    );
    return response.data;
  }

  async getStats(teamId: string): Promise<TeamSubscriptionStats> {
    const response = await api.get<TeamSubscriptionStats>(
      `/teams/${teamId}/subscriptions/stats`,
    );
    return response.data;
  }

  async syncPayments(teamId: string): Promise<{ synced: number }> {
    const response = await api.post<{ synced: number }>(
      `/teams/${teamId}/subscriptions/sync`,
    );
    return response.data;
  }

  async getMyOverview(): Promise<MyTeamSubscriptionsOverview> {
    const response = await api.get<MyTeamSubscriptionsOverview>(
      "/me/team-subscriptions",
    );
    return response.data;
  }
}

export const teamSubscriptionService = new TeamSubscriptionService();
